import { Card, IconButton, Stack, Typography } from '@mui/joy';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { CheckboxInput, Input, SwitchInput } from 'core/components';
import { ExpandableSection } from 'core/components/ExpandableSection';
import { IPartFinderCatalogFacetCategory, IPartFinderCatalogFacets } from 'partFinder/types';
import { Color, Direction, Level, Size, Variant } from 'theme/enums';

const MANUFACTURERS_SHOWN = 8;

interface IProps {
  facets?: IPartFinderCatalogFacets;
  manufacturerIds: number[];
  categoryIds: number[];
  locationIds: number[];
  inStock: boolean;
  onManufacturerIdsChange: (ids: number[]) => void;
  onCategoryIdsChange: (ids: number[]) => void;
  onLocationIdsChange: (ids: number[]) => void;
  onInStockChange: (inStock: boolean) => void;
}

const toggle = (ids: number[], id: number) =>
  ids.includes(id) ? ids.filter((value) => value !== id) : [...ids, id];

export const CatalogFilters = ({
  facets,
  manufacturerIds,
  categoryIds,
  locationIds,
  inStock,
  onManufacturerIdsChange,
  onCategoryIdsChange,
  onLocationIdsChange,
  onInStockChange,
}: IProps) => {
  const { t } = useTranslation();
  const { user } = useAuthUser();

  const [manufacturerSearch, setManufacturerSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [showAllManufacturers, setShowAllManufacturers] = useState(false);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const myLocationId = user?.location?.id ? Number(user.location.id) : null;

  const manufacturers = useMemo(() => {
    const search = manufacturerSearch.trim().toLowerCase();
    const all = (facets?.manufacturers ?? []).filter(({ name }) =>
      name.toLowerCase().includes(search)
    );

    return showAllManufacturers || search ? all : all.slice(0, MANUFACTURERS_SHOWN);
  }, [facets, manufacturerSearch, showAllManufacturers]);

  const locations = useMemo(() => {
    const search = locationSearch.trim().toLowerCase();

    return (facets?.locations ?? []).filter(({ name }) => name.toLowerCase().includes(search));
  }, [facets, locationSearch]);

  const categoriesByParent = useMemo(() => {
    const map = new Map<number | null, IPartFinderCatalogFacetCategory[]>();

    (facets?.categories ?? []).forEach((category) => {
      map.set(category.parentId, [...(map.get(category.parentId) ?? []), category]);
    });

    return map;
  }, [facets]);

  const renderCategories = (parentId: number | null, depth = 0) =>
    (categoriesByParent.get(parentId) ?? []).map((category) => {
      const children = categoriesByParent.get(category.id) ?? [];
      const isExpanded = expandedIds.includes(category.id);
      const isPicked = categoryIds.includes(category.id);

      return (
        <Stack key={category.id} sx={{ gap: 1.5, pl: depth ? 3 : 0 }}>
          <Stack
            direction={Direction.ROW}
            sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
          >
            <CheckboxInput
              size={Size.SM}
              label={
                category.familiesCount
                  ? `${category.name} (${category.familiesCount})`
                  : category.name
              }
              value={isPicked}
              onChange={() => onCategoryIdsChange(toggle(categoryIds, category.id))}
            />
            {children.length > 0 && (
              <IconButton
                size={Size.SM}
                variant={Variant.PLAIN}
                sx={{ minHeight: 0, height: 20, width: 20 }}
                onClick={() => setExpandedIds((ids) => toggle(ids, category.id))}
              >
                {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
              </IconButton>
            )}
          </Stack>
          {isExpanded && renderCategories(category.id, depth + 1)}
        </Stack>
      );
    });

  return (
    <Card sx={{ width: 300, flexShrink: 0, minHeight: 0, overflowY: 'auto', gap: 2 }}>
      <Typography level={Level.TITLE_LG}>{t('titlePartFinderFilters')}</Typography>

      <Stack
        direction={Direction.ROW}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography level={Level.TITLE_MD}>{t('labelInStock')}</Typography>
        <SwitchInput
          checked={inStock}
          onChange={(event) => onInStockChange(event.target.checked)}
        />
      </Stack>

      <ExpandableSection title={t('headerManufacturer')}>
        <Stack sx={{ gap: 1.5 }}>
          <Input
            size={Size.SM}
            placeholder={t('labelSearch')}
            value={manufacturerSearch}
            onChange={(event) => setManufacturerSearch(event.target.value)}
          />
          {manufacturers.map(({ id, name, partsCount }) => (
            <CheckboxInput
              key={id}
              size={Size.SM}
              label={`${name} (${partsCount.toLocaleString()})`}
              value={manufacturerIds.includes(id)}
              onChange={() => onManufacturerIdsChange(toggle(manufacturerIds, id))}
            />
          ))}
          {!manufacturerSearch && (facets?.manufacturers.length ?? 0) > MANUFACTURERS_SHOWN && (
            <Typography
              level={Level.BODY_SM}
              color={Color.PRIMARY}
              sx={{ cursor: 'pointer' }}
              onClick={() => setShowAllManufacturers((value) => !value)}
            >
              {showAllManufacturers
                ? t('buttonLabelSeeLess')
                : t('buttonLabelSeeAllOptions', { count: facets?.manufacturers.length })}
            </Typography>
          )}
        </Stack>
      </ExpandableSection>

      {(facets?.locations?.length ?? 0) > 0 && (
        <ExpandableSection title={t('labelShippingLocation')}>
          <Stack sx={{ gap: 1.5 }}>
            <Input
              size={Size.SM}
              placeholder={t('labelSearch')}
              value={locationSearch}
              onChange={(event) => setLocationSearch(event.target.value)}
            />
            <Stack sx={{ gap: 1.5, maxHeight: 280, overflowY: 'auto', pr: 1 }}>
              {!locationSearch && (
                <>
                  <CheckboxInput
                    size={Size.SM}
                    label={t('labelAnyLocations')}
                    value={!locationIds.length}
                    onChange={() => onLocationIdsChange([])}
                  />
                  {myLocationId !== null && (
                    <CheckboxInput
                      size={Size.SM}
                      label={t('labelMyLocation')}
                      value={locationIds.includes(myLocationId)}
                      onChange={() => onLocationIdsChange(toggle(locationIds, myLocationId))}
                    />
                  )}
                </>
              )}
              {locations.map(({ id, name }) => (
                <CheckboxInput
                  key={id}
                  size={Size.SM}
                  label={name}
                  value={locationIds.includes(id)}
                  onChange={() => onLocationIdsChange(toggle(locationIds, id))}
                />
              ))}
            </Stack>
          </Stack>
        </ExpandableSection>
      )}

      {(facets?.categories.length ?? 0) > 0 && (
        <ExpandableSection title={t('headerCategories')}>
          <Stack sx={{ gap: 1.5 }}>{renderCategories(null)}</Stack>
        </ExpandableSection>
      )}
    </Card>
  );
};
